// lib/viewMode.ts
// One grid/list choice for the whole search UI, remembered on the device.
//
// The home screen, the results and the chat each used to keep their OWN view
// state, all starting on 'grid'. A shopper who picked list on one screen was put
// back on grid by the next (syfak, 2026-09-28: «όταν αλλάζει ο χρήστης και ψάχνει
// ξανά γυρίζει πάντα στην προεπιλεγμένη»). SearchWidget now owns the single state
// and hands it to all three.
//
// Why localStorage and not just React state: syfak opens products in the SAME
// window (`ui.productLinkTarget: '_self'`), so every product click reloads the
// page and would reset any in-memory choice. Keyed per client, like recent
// searches, so our shared /demo origin doesn't bleed one client's choice into
// another's.

import { useCallback, useEffect, useState } from 'react';

export type ViewMode = 'grid' | 'list';

function isViewMode(v: unknown): v is ViewMode {
  return v === 'grid' || v === 'list';
}

export function useViewMode(clientId: string, clientDefault: ViewMode | undefined) {
  const fallback: ViewMode = clientDefault ?? 'grid';
  const storageKey = `viewMode:${clientId}`;
  const [viewMode, setViewModeState] = useState<ViewMode>(fallback);

  // Read after mount, not in the initializer: demo pages render on the server,
  // where there is no localStorage, and a different first render would mismatch.
  useEffect(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (isViewMode(stored)) setViewModeState(stored);
    } catch {
      // Storage blocked (private mode, sandboxed host) — the client default stands.
    }
  }, [storageKey]);

  const setViewMode = useCallback(
    (mode: ViewMode) => {
      setViewModeState(mode);
      try {
        localStorage.setItem(storageKey, mode);
      } catch {
        // Not remembered across reloads, but still applied for this session.
      }
    },
    [storageKey],
  );

  return [viewMode, setViewMode] as const;
}
